import React, { useEffect, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'

import HFDropdown from 'components/HFInputs/HFDropdown'
import HFTextInput from 'components/HFInputs/HFTextInput'
import HFOptionGroup from 'components/HFInputs/HFOptionGroup'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { useFetchList } from 'store/hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { ClinicalProcess, FinalSituation } from 'models'
import { FinalSituationSchema } from './FinalSituationSchema'

import styles from '../Dialog.module.sass'
import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'
import useSubmitTask from 'hooks/useSubmitTask'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import AlertBox from 'components/AlertBox/AlertBox'
import DetailBox from 'components/DetailBox/DetailBox'
import InfoList from 'components/InfoList/InfoList'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'

const TIME_BEFORE_NEXT_ACCD_EVALUATION = '45'
const TIME_BEFORE_NEXT_ACSD_EVALUATION = '60'

const removeEmpty = (obj) => {
	return Object.fromEntries(
		Object.entries(obj)
			.filter(([_, v]) => v !== null)
			.map(([k, v]) => [k, v === Object(v) ? removeEmpty(v) : v])
	)
}

const FinalSituationTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)

	const booleanOptions = [
		{
			value: true,
			text: i18n`Yes`,
		},
		{
			value: false,
			text: i18n`No`,
		},
	]

	const accdNextEvaluationDays = [
		{
			value: '15',
			text: '15',
		},
		{
			value: '30',
			text: '30',
		},
		{
			value: '45',
			text: '45',
		},
		{
			value: '60',
			text: '60',
		},
		{
			value: '90',
			text: '90',
		},
	]

	const acsdNextEvaluationDays = [
		{
			value: '60',
			text: '60',
		},
	]

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(FinalSituationSchema({ ...argValues })),
		shouldUnregister: true,
	})

	const { handleSubmit, formState, watch, reset, getValues, setValue } =
		methods

	const [disabilityType, PPDWeight, predictedPPD, treatmentDays] = watch([
		'disability.type',
		'disability.PPDWeight',
		'disability.predictedPPD',
		'treatmentDays',
	])

	const { error, submitting, approveTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const {
		state,
		contents: [
			clinicalProcess,
			lastFinalSituation,
			ppdWeights,
			finalSituationOptions,
		],
	} = useFetchList(
		[
			`clinical_processes/${data?.processId}`,
			`clinical_processes/${data?.processId}/final_situations/last`,
			`metadata/ppd_weights`,
			`metadata/final_situation_options`,
		],
		[ClinicalProcess.fromJSON, FinalSituation.fromJSON]
	)
	const isLoading = state === 'loading'

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useEffect(() => {
		setValue(
			'timeBeforeNextEvaluation',
			disabilityType === 'ACCD'
				? TIME_BEFORE_NEXT_ACCD_EVALUATION
				: TIME_BEFORE_NEXT_ACSD_EVALUATION,
			{
				shouldValidate: false,
				shouldDirty: false,
			}
		)
	}, [disabilityType, setValue])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}
			if (finalSituationOptions || lastFinalSituation || ppdWeights) {
				defaultValues = {
					...defaultValues,
					'disability.type':
						lastFinalSituation?.disability?.value ||
						finalSituationOptions?.[0]?.value,
					'disability.biologicalDamage':
						lastFinalSituation?.expectedSituation?.biologicalDamage,
					'disability.predictedPPD':
						lastFinalSituation?.expectedSituation
							?.permanentPartialDisabilityRate,
					'disability.PPDWeight':
						lastFinalSituation?.expectedSituation?.weightOption
							?.value || ppdWeights?.[0]?.value,
					'disability.PTDOW':
						lastFinalSituation?.expectedSituation
							?.isPermanentTotalDisabilityForOrdinaryWork,
					'disability.needForAdditionalEfforts':
						lastFinalSituation?.expectedSituation
							?.additionalEfforts,
					treatmentDays:
						lastFinalSituation?.expectedSituation?.treatmentDays,
					'ttd.totalDays':
						lastFinalSituation?.expectedSituation
							?.temporaryTotalDisabilityDays,
					'ttd.justification': lastFinalSituation?.justification,
				}

				setArgValues({
					accidentSegmentId: clinicalProcess?.accident?.segment?.id,
					lastClinicalEpisode:
						clinicalProcess?.lastClinicalEpisode?.date?.daysFromNow,
				})
			}
			defaultValues = {
				...defaultValues,
				timeBeforeNextEvaluation:
					disabilityType === 'ACCD'
						? TIME_BEFORE_NEXT_ACCD_EVALUATION
						: TIME_BEFORE_NEXT_ACSD_EVALUATION,
			}

			setDefaultValues({ ...defaultValues })
		}
	}, [
		isLoading,
		finalSituationOptions ?? {},
		lastFinalSituation ?? {},
		ppdWeights ?? {},
	])

	const onSubmit = (formData) => {
		const body = removeEmpty(formData)

		approveTask(body, 'submit')
			.then(() => resolve())
			.catch(() => {})
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			)}

			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController task={data} />
					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						{data?.subType === 'ManualIPP' && (
							<InfoList
								items={[
									{
										title: i18n`Requested By`,
										text:
											clinicalProcess?.manualRequestUser
												?.name || '-',
										icon: 'profile',
									},
									{
										title: i18n`Reason`,
										text:
											clinicalProcess?.manualPrevisionObservation ||
											'-',
										icon: 'info',
									},
								]}
							/>
						)}

						<fieldset>
							<h4>{i18n`Final Situation Prediction`}</h4>
							<VStack className={styles['fieldset-item']}>
								<h5
									className={styles['input-title']}
								>{i18n`Disability type`}</h5>
								<HFDropdown
									name='disability.type'
									placeholder=''
									options={finalSituationOptions || []}
									displayValue={true}
									searchable={true}
									optionsAlign='right'
									noData={i18n`No results`}
								/>
							</VStack>
							{disabilityType === 'ACCD' &&
								![
									'WORK_RELATED',
									'SPORTS_ACCIDENT',
									'LIFE_INSURANCE',
								].includes(
									clinicalProcess?.accident?.segment?.id
								) && (
									<>
										<h5
											className={styles['input-title']}
										>{i18n`Biological Damage (XX.XX)`}</h5>
										<HFTextInput
											name='disability.biologicalDamage'
											type='number'
											placeholder=''
											inputMode={'numeric'}
											multiline={false}
											optionsAlign='right'
											noResize
										/>
									</>
								)}
							{disabilityType === 'ACCD' &&
								[
									'WORK_RELATED',
									'SPORTS_ACCIDENT',
									'LIFE_INSURANCE',
								].includes(
									clinicalProcess?.accident?.segment?.id
								) && (
									<VStack gap='xsmall'>
										<HStack
											className={styles['ppd-container']}
											align='center'
											wrap='nowrap'
											gap='small'
											justify='space-between'
										>
											<div>
												<h5
													className={
														styles['input-title']
													}
												>
													{i18n`PPD`} (%)
												</h5>
												<HFTextInput
													name='disability.predictedPPD'
													type='number'
													inputMode={'numeric'}
													pattern={
														'^100$|^[0-9]{1,2}([.][0-9]{1,2})?$'
													}
												/>
											</div>
											<div>
												<h5
													className={
														styles['input-title']
													}
												>{i18n`PPD Weight`}</h5>
												<HFDropdown
													name='disability.PPDWeight'
													placeholder=''
													options={ppdWeights || []}
													searchable={true}
													optionsAlign='right'
													noData={i18n`No results`}
												/>
											</div>
										</HStack>
										<DetailBox
											className={
												styles['--gray-background']
											}
											title={i18n`Final PPD`}
											text={i18n`PPD x PPD Weight`}
											value={`${parseFloat(
												Math.min(100, PPDWeight || 0) *
													parseFloat(
														predictedPPD || 0
													)
											).toFixed(2)} %`}
										/>
									</VStack>
								)}
						</fieldset>

						{disabilityType === 'ACCD' && (
							<fieldset>
								<VStack gap='xsmall'>
									<HFOptionGroup
										className={`${styles['option-group']} ${styles['--horizontal-options']}`}
										name='disability.PTDOW'
										type='boolean'
										layout='horizontal'
										label={i18n`PTDOW?`}
										options={booleanOptions}
									/>
									<HFOptionGroup
										className={`${styles['option-group']} ${styles['--horizontal-options']}`}
										name='disability.needForAdditionalEfforts'
										type='boolean'
										layout='horizontal'
										label={i18n`Need for additional efforts?`}
										options={booleanOptions}
									/>
								</VStack>
							</fieldset>
						)}

						<fieldset>
							<h4>{i18n`Treatment`}</h4>
							<VStack gap='xsmall'>
								<HFTextInput
									name='treatmentDays'
									type='number'
									placeholder={i18n`Total treatment days`}
									inputMode={'numeric'}
									multiline={false}
									noResize
								/>
								<DetailBox
									title={i18n`Days in treatment`}
									text={i18n`Days on treatment since beginning of episode`}
									value={
										clinicalProcess?.lastClinicalEpisode
											?.date?.daysFromNow
									}
								/>
								<DetailBox
									className={styles['--gray-background']}
									title={i18n`Remaining days`}
									text={i18n`Forecast - days in treatment`}
									value={
										treatmentDays
											? treatmentDays -
											  clinicalProcess
													?.lastClinicalEpisode?.date
													?.daysFromNow
											: ''
									}
								/>
							</VStack>
						</fieldset>

						<fieldset>
							<h4>{i18n`TTD`}</h4>
							<VStack gap='xsmall'>
								<div>
									<h5
										className={styles['input-title']}
									>{i18n`Prevision of days with TTD`}</h5>
									<HFTextInput
										name='ttd.totalDays'
										type='number'
										placeholder=''
										inputMode={'numeric'}
									/>
									<small
										className={styles['field-description']}
									>
										{i18n`Prevision from now`}
									</small>
								</div>

								<div>
									<h5
										className={styles['input-title']}
									>{i18n`Justification`}</h5>
									<HFTextInput
										name='ttd.justification'
										placeholder={i18n`Write the reasons for your decision`}
										multiline={true}
										rows={5}
										noResize
									/>
								</div>
							</VStack>
						</fieldset>
						<fieldset>
							<h4>{i18n`Next evaluation`}</h4>
							<HFOptionGroup
								className={`${styles['option-group']} ${styles['--horizontal-options']}`}
								name='timeBeforeNextEvaluation'
								label={i18n`Number of days until next evaluation`}
								options={
									disabilityType === 'ACCD'
										? accdNextEvaluationDays
										: acsdNextEvaluationDays
								}
							/>
						</fieldset>
					</form>
				</div>
			</FormProvider>
			<TaskMenuController
				data={{
					formId: id,
					options: ['postpone', 'forward'],
					submitting,
					submissionType: 'submit',
					getValues,
				}}
			/>
		</>
	)
}

export default FinalSituationTaskController
